// Longitudinal profile — a node-to-node path through the network's links
// (BFS, undirected — a profile can run against nominal flow direction
// too), with each node's invert/ground elevation and (at one report
// timestamp) the water surface (Head) — SWMM's own three profile lines.
//
// Deliberately traces the shortest path (fewest links) between two nodes:
// this graph doesn't let a user pick a specific one of several possible
// routes.
//
// "Ground" is each node's invert + declared MaxDepth (`[JUNCTIONS]`/
// `[STORAGE]`'s MaxDepth column, both at raw index 2). An OUTFALLS/DIVIDERS
// node has no such column, so its ground is just its invert.

import type { SwmmProject } from "../model/inp/types.ts";
import { readInvertElevations } from "./routing/routing.ts";
import {
  fromFeet,
  readFlowUnits,
  toFeet,
  unitSystemOf,
  type Feet,
  type UnitSystemName,
} from "../model/units.ts";

interface ProfilePoint {
  node: string;
  /** Cumulative from the start node, project's own length unit. */
  distance: number;
  invert: number;
  ground: number;
  /** The water surface (Head) at this node for whichever report timestamp the caller asked for — `null` when this node has no result there (e.g. a project with no simulation run yet). */
  head: number | null;
}

/** BFS shortest path (fewest links) of node names from `start` to `end`, undirected. `null` when they're simply not connected at all — a project can have more than one disjoint subnetwork. */
export function findProfilePath(project: SwmmProject, start: string, end: string): string[] | null {
  if (start === end) return [start];
  const adjacency = new Map<string, string[]>();
  const addEdge = (a: string, b: string) => {
    const list = adjacency.get(a);
    if (list) list.push(b);
    else adjacency.set(a, [b]);
  };
  for (const link of project.links) {
    addEdge(link.fromNode, link.toNode);
    addEdge(link.toNode, link.fromNode);
  }

  const parent = new Map<string, string>();
  const visited = new Set<string>([start]);
  const queue: string[] = [start];
  while (queue.length > 0) {
    const current = queue.shift()!;
    if (current === end) break;
    for (const next of adjacency.get(current) ?? []) {
      if (visited.has(next)) continue;
      visited.add(next);
      parent.set(next, current);
      queue.push(next);
    }
  }
  if (!visited.has(end)) return null;

  const path = [end];
  let node = end;
  while (node !== start) {
    node = parent.get(node)!;
    path.push(node);
  }
  return path.reverse();
}

/** This path's [JUNCTIONS]/[STORAGE] MaxDepth, ft, both at raw index 2. Missing/other node kinds fall back to 0 (ground = invert). */
function readMaxDepthFt(
  raw: ReadonlyMap<string, string[][]>,
  sys: UnitSystemName
): Map<string, number> {
  const result = new Map<string, number>();
  for (const section of ["JUNCTIONS", "STORAGE"]) {
    for (const row of raw.get(section) ?? []) {
      if (row[0] && row[2] !== undefined) result.set(row[0], toFeet(Number(row[2]), sys));
    }
  }
  return result;
}

/** This path's conduit length between two adjacent nodes, ft — 0 for a non-CONDUIT link (no comparable "length" column) or no direct link at all (shouldn't happen for a path `findProfilePath` returned, but a defensive fallback rather than a crash). */
function directLinkLengthFt(
  project: SwmmProject,
  raw: ReadonlyMap<string, string[][]>,
  sys: UnitSystemName,
  a: string,
  b: string
): number {
  const link = project.links.find(
    (l) => (l.fromNode === a && l.toNode === b) || (l.fromNode === b && l.toNode === a)
  );
  if (!link) return 0;
  const row = (raw.get("CONDUITS") ?? []).find((r) => r[0] === link.name);
  if (!row) return 0;
  return toFeet(Number(row[3] ?? "0"), sys);
}

/**
 * Builds the profile points for `path` (as returned by `findProfilePath`)
 * — invert/ground in the project's own length unit throughout, Head read
 * from `nodeHead` (`SimulationResults`, already in that same unit) at
 * `timeIndex`, `null` when that node/index has no result.
 */
export function computeLongitudinalProfile(
  project: SwmmProject,
  path: string[],
  nodeHead: Map<string, number[]>,
  timeIndex: number
): ProfilePoint[] {
  const raw = project.rawSections;
  const flowUnits = readFlowUnits(raw);
  const sys = unitSystemOf(flowUnits);
  const invertFt = readInvertElevations(raw, sys);
  const maxDepthFt = readMaxDepthFt(raw, sys);

  const points: ProfilePoint[] = [];
  let distanceFt = 0;
  for (let i = 0; i < path.length; i++) {
    const node = path[i];
    if (i > 0) distanceFt += directLinkLengthFt(project, raw, sys, path[i - 1], node);
    const invert = fromFeet((invertFt.get(node) ?? 0) as Feet, sys);
    const ground = fromFeet(((invertFt.get(node) ?? 0) + (maxDepthFt.get(node) ?? 0)) as Feet, sys);
    const head = nodeHead.get(node)?.[timeIndex] ?? null;
    points.push({ node, distance: fromFeet(distanceFt as Feet, sys), invert, ground, head });
  }
  return points;
}
